import { StatusCodes } from 'http-status-codes'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { getEntryStep, getFlowConfig } from '@ngb-frontend/features/flow-config'
import { useFlow } from '@ngb-frontend/flow'
import { useContent, useQuery, useStorage } from '@ngb-frontend/shared/context'
import {
  ErrorVariants,
  Feature,
  SystemCode,
  type BasicFeatureStep,
  type QueryParams,
} from '@ngb-frontend/shared/types'
import {
  FullScreenLoader,
  StepLayout as Layout,
  boundaryErrors,
} from '@ngb-frontend/shared/ui'
import {
  getMnRDCCFlowDataFromBooking,
  getEoLDCCFlowDataFromBooking,
  getMnRFlowDataFromBooking,
  isDynamicStep,
  isQuery,
  omitNull,
  useBookingRequest,
  useBookingRequestHistory,
  useDriver,
  useVehicle,
  isLPSystem,
} from '@ngb-frontend/shared/utils'

import type { GetServerSideProps, NextPage } from 'next'

export type PageProps = { query: QueryParams | null }

const Index: NextPage<PageProps> = () => {
  const c = useContent()
  const { getLocalStorage, setLocalStorage } = useStorage()
  const router = useRouter()
  const query = useQuery()
  const isRoutingToAppointments = useRef(false)
  const bookingHistoryChecked = !!getLocalStorage('bookingHistoryChecked')
  const { hasData: hasBookingHistory, isLoading: bookingHistoryLoading } =
    useBookingRequestHistory(
      () => !!getLocalStorage('bookingHistoryChecked'),
      () => setLocalStorage('bookingHistoryChecked', true),
      !query?.bookingId ? query?.vehicleIdentifier : undefined,
      undefined,
      query?.country,
    )

  const fConfig = getFlowConfig(query?.systemCode, query?.service)
  // Drivers see their appointments on the dashboard step
  const showExistingAppointments =
    fConfig.type !== Feature.MnR && hasBookingHistory && !bookingHistoryChecked

  const {
    data: bookingData,
    error: bookingError,
    isLoading: bookingLoading,
  } = useBookingRequest(query?.bookingId)

  useEffect(() => {
    if (showExistingAppointments && !isRoutingToAppointments.current) {
      router.push('/existing-appointment')
      isRoutingToAppointments.current = true
    }
  }, [router, showExistingAppointments])

  const pathname = window.location.pathname

  const preventReplaceUrl =
    (pathname.includes('summary') && window.location.search) ||
    isRoutingToAppointments.current

  const replaceUrl =
    (pathname === '/' || window.location.search) && !preventReplaceUrl

  // The following routing logic is not put in useEffect to run before initial render
  if (query && replaceUrl) {
    const entryStep = getEntryStep(fConfig, query.bookingId)
    router.replace(entryStep.path, undefined, {
      shallow: true,
    })
  }

  const stepPath = React.useMemo(() => pathname.replace(/^\//, ''), [pathname])

  const {
    data: vehicle,
    isLoading: vehicleLoading,
    error: vehicleError,
  } = useVehicle(query)

  const {
    data: driver,
    isLoading: driverLoading,
    error: driverError,
  } = useDriver(vehicle?.driverIdentifier || query?.licensePlate, query)

  const initialFlowData = useMemo(() => {
    const booking = bookingData?.[0]
    if (!booking) return []

    switch (fConfig.type) {
      case Feature.MnR:
        return getMnRFlowDataFromBooking(booking)
      case Feature.MnRDCC:
        return getMnRDCCFlowDataFromBooking(booking)
      case Feature.EoLDCC:
        return getEoLDCCFlowDataFromBooking(booking)
      default:
        return []
    }
  }, [bookingData, fConfig.type])

  // Get step
  // The flow's reroute is held while the entry redirect above is pending: it
  // navigates without shallow routing, which a later click cannot cancel, so
  // it would otherwise land after the driver has already moved on.
  const flowApi = useFlow(
    fConfig?.steps || [],
    stepPath,
    router,
    '',
    initialFlowData,
    bookingLoading || isRoutingToAppointments.current || !!replaceUrl,
  )
  const stepIndex = flowApi.stepIndex
  const activeStep = fConfig?.steps[stepIndex]
  const stepCount = fConfig?.steps.length
  // Merge into a new object: mutating the shared flow-config step (previous
  // Object.assign(activeStep, ...)) leaked variant overrides (e.g. the
  // Overview summary footer) into later renders of the base variant.
  const activeStepVariant =
    flowApi.stepVariant && isDynamicStep(activeStep, flowApi.stepVariant)
      ? { ...activeStep, ...activeStep.variant[flowApi.stepVariant] }
      : (activeStep as BasicFeatureStep<Feature>) // safe
  const Component = activeStepVariant?.component

  const onClose = useCallback(
    () => router.push(query?.closeUrl || ''),
    [query?.closeUrl, router],
  )

  const loadingCtx = bookingHistoryLoading || vehicleLoading || driverLoading

  useEffect(() => {
    if (
      stepPath &&
      !activeStep &&
      !isRoutingToAppointments.current &&
      stepPath !== query?.returnUrl &&
      stepPath !== query?.closeUrl
    ) {
      throw new Error(boundaryErrors.unknownPath)
    }
  }, [activeStep, query?.closeUrl, query?.returnUrl, stepPath])

  if (
    isRoutingToAppointments.current ||
    !activeStep ||
    bookingLoading ||
    loadingCtx
  ) {
    return <FullScreenLoader />
  }

  let ErrorVariant: ErrorVariants | null = null
  // A retry only helps when a bootstrapping request failed unexpectedly (a
  // transient/server error, not a 404 "record not found" or a mapped business
  // error). In those cases the error page offers "Retry", which reloads the app
  // at the first step and re-runs these session calls with the same params.
  let retryable = false
  if (isLPSystem(query?.systemCode) && vehicleError) {
    const isKnownVariant = Object.values(ErrorVariants).includes(
      vehicleError.info,
    )
    ErrorVariant = isKnownVariant ? vehicleError.info : ErrorVariants.Vehicle
    retryable = !isKnownVariant && vehicleError.status !== StatusCodes.NOT_FOUND
  } else if (!bookingData && bookingError) {
    ErrorVariant = ErrorVariants.FetchBooking
    retryable = bookingError.status !== StatusCodes.NOT_FOUND
  } else if (!driver) {
    ErrorVariant = ErrorVariants.Driver
    retryable = !!driverError && driverError.status !== StatusCodes.NOT_FOUND
  }

  if (ErrorVariant) {
    router.push(`/error/${ErrorVariant}${retryable ? '?retry=true' : ''}`)
    return <FullScreenLoader />
  }

  const stepperIndex = activeStepVariant.stepperIndex

  return (
    <Layout
      // TODO: fallback to first step as active step (see useFlow.ts)
      navLabel={c(`${activeStepVariant.navLabel}`)}
      type={fConfig.type}
      topNavLabel={c('navigationBar.topLabel', {
        variables: {
          currentStep: `${stepIndex + 1}`,
          numberOfSteps: `${stepCount}`,
        },
      })}
      stepperIndex={stepperIndex}
      title={activeStepVariant.title && c(activeStepVariant.title)}
      subtitle={activeStepVariant.subtitle && c(activeStepVariant.subtitle)}
      onStepBack={
        activeStepVariant.routeToBack
          ? router.back
          : flowApi?.hasPreviousStep
          ? flowApi.onPreviousStep
          : onClose
      }
      progress={(100 * stepIndex) / stepCount}
      onClose={onClose}
      fullWidth={activeStep.fullWidth}
      narrow={activeStep.narrow}
      footer={activeStepVariant.footer}
      navigation={activeStepVariant.navigation}
    >
      {replaceUrl || flowApi.skipEnabled ? (
        <FullScreenLoader />
      ) : (
        <Component
          booking={bookingData?.[0]}
          // @ts-ignore
          variant={activeStepVariant?.['flowVariant']}
          flow={fConfig.type}
          {...flowApi}
        />
      )}
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query: q }) => {
  // Extract only the values we need
  const {
    returnUrl,
    service,
    ilan = null,
    bookingId = null,
    agentId = null,
    registration = null,
    caseId = null,
    closeUrl = q.returnUrl,
    country = null,
    systemCode = SystemCode.MYLP,
    tenant = null,
  } = q

  const query = isQuery({ ...q, systemCode })
    ? omitNull({
        ilan,
        returnUrl,
        service,
        country,
        agentId,
        bookingId,
        caseId,
        systemCode,
        licensePlate: registration,
        closeUrl,
        tenant,
      })
    : null

  return { props: { query } }
}

export default Index
